type LegalPageKind = "privacy" | "terms";

const LEGAL_CONTENT = {
	privacy: {
		eyebrow: "Privacy Policy",
		title: "How Clapio handles website and app data",
		updated: "Last updated: April 24, 2026",
		intro:
			"This page explains what information Clapio may collect when you visit the website, download the app, contact support, or use related services.",
		sections: [
			{
				heading: "Information we collect",
				body: "We may collect basic analytics about website visits, device and browser information, download activity, and any information you voluntarily send us through support forms or email.",
			},
			{
				heading: "How we use information",
				body: "We use this information to operate the website, understand demand for features and platforms, improve product experience, respond to support requests, and maintain the security of our services.",
			},
			{
				heading: "Third-party services",
				body: "Clapio may rely on third-party providers for analytics, hosting, software distribution, and payment processing. Those providers may process information according to their own privacy terms when you interact with their services.",
			},
			{
				heading: "Data retention",
				body: "We retain information only for as long as it is reasonably needed for service operations, legal compliance, support, security, and business recordkeeping.",
			},
			{
				heading: "Your choices",
				body: "You can contact us to ask about access, correction, or deletion requests related to information you have submitted directly to us. Some data may still be retained where required for legitimate business or legal reasons.",
			},
		],
	},
	terms: {
		eyebrow: "Terms of Service",
		title: "Basic terms for using Clapio",
		updated: "Last updated: April 24, 2026",
		intro:
			"These terms describe the basic rules for accessing the Clapio website, downloads, and related software services.",
		sections: [
			{
				heading: "Use of the service",
				body: "You may use Clapio only in compliance with applicable laws and these terms. You are responsible for the content you record, upload, or share using the software.",
			},
			{
				heading: "Accounts, purchases, and subscriptions",
				body: "Paid features, subscriptions, and renewals may be managed through third-party payment or subscription providers. Pricing, billing cycles, and cancellation terms shown at checkout control your purchase.",
			},
			{
				heading: "Intellectual property",
				body: "Clapio, its website, branding, software, and related materials remain the property of their respective owners. These terms do not transfer ownership of the software or website content to you.",
			},
			{
				heading: "Acceptable use",
				body: "You may not use Clapio to violate laws, infringe rights, distribute malware, abuse the service, or interfere with the operation or security of the product and website.",
			},
			{
				heading: "Availability and changes",
				body: "We may update, improve, suspend, or discontinue parts of the website or software from time to time. We do not guarantee uninterrupted availability of every feature or download.",
			},
		],
	},
} as const;

export function LegalPage({ kind }: { kind: LegalPageKind }) {
	const content = LEGAL_CONTENT[kind];

	return (
		<div className="mx-auto max-w-4xl px-6 py-12 md:py-20">
			<a
				href="/"
				className="inline-flex items-center rounded-full border border-white/[0.08] bg-white/[0.03] px-4 py-2 text-sm text-white/70 transition-colors hover:text-white"
			>
				← Back to Clapio
			</a>

			<main className="mt-8 rounded-[2rem] border border-white/[0.08] bg-white/[0.03] p-8 backdrop-blur-xl md:p-12">
				<p className="text-[11px] uppercase tracking-[0.22em] text-brand-300">{content.eyebrow}</p>
				<h1 className="mt-4 max-w-3xl text-4xl font-extrabold tracking-tight text-white md:text-6xl">
					{content.title}
				</h1>
				<p className="mt-4 text-sm text-white/40">{content.updated}</p>
				<p className="mt-6 max-w-3xl text-base leading-8 text-white/60 md:text-lg">
					{content.intro}
				</p>

				<div className="mt-12 space-y-8">
					{content.sections.map((section) => (
						<section key={section.heading} className="glass-card p-6 md:p-7">
							<h2 className="text-2xl font-semibold tracking-tight text-white/90">
								{section.heading}
							</h2>
							<p className="mt-4 text-sm leading-8 text-white/55 md:text-base">{section.body}</p>
						</section>
					))}
				</div>

				<div className="mt-12 rounded-[1.5rem] border border-white/[0.08] bg-black/10 p-6">
					<h2 className="text-xl font-semibold tracking-tight text-white/90">Contact</h2>
					<p className="mt-3 text-sm leading-7 text-white/55 md:text-base">
						If you have privacy or terms questions, contact
						<a
							href="mailto:hello@learn2vibecode.dev"
							className="ml-1 text-brand-300 transition-colors hover:text-brand-200"
						>
							hello@learn2vibecode.dev
						</a>
						.
					</p>
				</div>
			</main>
		</div>
	);
}
